/**
 * Game-data import.
 *
 * Three sources, one pipeline: every source produces a list of
 * {path, size, open()} descriptors, which are filtered, validated, and written
 * into the vault in batches. Nothing is read into memory until it is written,
 * and .dat files go in as Blob references, so importing a 500MB install never
 * materialises 500MB of JS heap.
 */

import type { GameType } from '../config';
import { registerGame, vaultDbName } from '../registry';
import { deleteFiles, getMeta, listFiles, putFiles, setMeta, useVault, type InstallMeta, type VaultEntry } from '../store';
import { readCentralDirectory, entryBlob, ZipReadError } from '../zip/read';
import { DatError, acceptDat, readDat2Info, type Dat2Info } from './dat';
import { sniffDat1 } from './dat1';
import { RPU_META_KEY, type RpuMeta } from './rpu';

export interface SourceFile {
  /** Path relative to the game root, forward slashes. */
  path: string;
  size: number;
  open(): Promise<Blob>;
}

export interface ImportProgress {
  stage: string;
  file?: string;
  bytesDone: number;
  bytesTotal: number;
  filesDone: number;
  filesTotal: number;
}

export interface ImportResult {
  meta: InstallMeta;
  warnings: string[];
  /** Mod-loader / legacy-launcher config that was deliberately left behind. */
  quarantined: string[];
}

export class ImportError extends Error {}

const REQUIRED_DATS = ['master.dat', 'critter.dat'];
const OPTIONAL_DATS = ['patch000.dat'];

/**
 * Config belonging to software we do not ship.
 *
 * A desktop install is usually layered: sfall (ddraw.dll + ddraw.ini) and the
 * Hi-Res patch (f2_res.dll + f2_res.ini) sit on top of the original binary.
 * fallout2-ce reimplements both natively and, on first run, *migrates* those
 * legacy files into its own config/game.cfg — see engine/src/content_config.cc
 * and engine/src/game_config_migration.cc. Carrying them into the vault means
 * carrying settings written for a different renderer, and a migration whose
 * output then outlives whatever we generate. We author fallout2.cfg and
 * f2_res.ini ourselves, so the legacy originals have nothing to offer.
 *
 * f2_res.dat is the exception and is kept: the Hi-Res patch's *art* pack is
 * genuinely read by CE, which inserts settings.system.f2_res_dat_path into the
 * database stack (engine/src/game.cc, gameDbInit).
 */
const QUARANTINE_NAMES = new Set([
  'ddraw.ini', // sfall
  'ddraw.dll',
  'sfall.ini',
  'sfall.dll',
  'f2_res.ini', // Hi-Res patch
  'f2_res.dll',
  'f2_res_config.exe',
  'fallout2.cfg', // we generate our own
  'fallout2hr.cfg',
  'game.cfg', // engine-side migration output; see purgeQuarantined()
  'game#patch.cfg',
]);

/** Native loaders and launchers. Nothing in a WASM build can execute these. */
const QUARANTINE_EXT = /\.(dll|exe|bat|cmd|com)$/;

/** True for files we refuse to vault even though they sit in a wanted location. */
export function isQuarantined(path: string): boolean {
  const p = path.toLowerCase();
  const name = p.slice(p.lastIndexOf('/') + 1);
  return QUARANTINE_NAMES.has(name) || QUARANTINE_EXT.test(name);
}

/**
 * The one context where quarantined config is *content*: a Restoration
 * Project install. RPU's scripts hard-fail unless its own ddraw.ini reads
 * back (`WorldMapSlots=21`), and `IniConfigFolder=mods` in it is how they
 * find their settings at all — so when the mods layer is present, ddraw.ini
 * is the mod's file, not a stale loader leftover. Everything else in the
 * quarantine stays quarantined regardless.
 */
export function hasModsLayer(paths: string[]): boolean {
  return paths.some((p) => /^mods\/[^/]+\.dat$/i.test(p));
}

function keptDespiteQuarantine(path: string, modsLayer: boolean): boolean {
  return modsLayer && path.toLowerCase() === 'ddraw.ini';
}

/** Files we keep. Everything else in a game folder is Windows cruft to us. */
function isWanted(path: string): boolean {
  const p = path.toLowerCase();
  if (p.split('/').some((seg) => seg.startsWith('.') || seg === '__macosx')) return false;
  if (/^[^/]+\.dat$/.test(p)) return true; // master/critter/patchNNN/f2_res at the root
  // The sfall mods layer: archives, their settings, and the load order the
  // engine reads (engine/src/sfall_ext.cc). A desktop install with the
  // Restoration Project applied imports whole this way.
  if (/^mods\/[^/]+\.(dat|ini)$/.test(p) || p === 'mods/mods_order.txt') return true;
  if (p.startsWith('data/')) {
    // The player's existing desktop saves are deliberately not imported: they
    // would collide with the browser save archive and double the import size.
    if (p.startsWith('data/savegame')) return false;
    return true;
  }
  return false;
}

/**
 * Anything a *previous* import let through, or that an engine-side migration
 * wrote into a location we vault. MEMFS is rebuilt from the vault on every
 * boot, so a stale entry here is not a one-off — it is reapplied forever.
 */
export function isContamination(path: string): boolean {
  const p = path.toLowerCase();
  return isQuarantined(p) || /^(data\/)?config\//.test(p);
}

/**
 * Finds the directory prefix that contains master.dat and strips it, so an
 * archive rooted at "Fallout 2/" or "GOG Games/Fallout 2/" imports the same as
 * one rooted at the game folder itself.
 */
function stripRoot(files: SourceFile[]): SourceFile[] {
  const master = files.find((f) => /(^|\/)master\.dat$/i.test(f.path));
  if (!master) return files;
  const idx = master.path.toLowerCase().lastIndexOf('master.dat');
  const prefix = master.path.slice(0, idx);
  if (!prefix) return files;
  return files
    .filter((f) => f.path.startsWith(prefix))
    .map((f) => ({ ...f, path: f.path.slice(prefix.length) }));
}

export function validate(files: SourceFile[], gameType: GameType): { warnings: string[] } {
  const names = new Set(files.map((f) => f.path.toLowerCase()));
  const label = gameType === 'fo1' ? 'Fallout 1' : 'Fallout 2';
  const missing = REQUIRED_DATS.filter((d) => !names.has(d));
  if (missing.length) {
    throw new ImportError(
      `Missing ${missing.join(' and ')}. Point us at your ${label} install folder — the one containing master.dat and critter.dat.`
    );
  }
  const warnings: string[] = [];
  if (gameType === 'fo2') {
    for (const d of OPTIONAL_DATS) {
      if (!names.has(d)) {
        warnings.push(
          `${d} is missing. That is the official 1.02 patch; the game runs without it, but some quests and fixes will be absent.`
        );
      }
    }
  }
  if (!files.some((f) => f.path.toLowerCase().startsWith('data/sound/music/'))) {
    warnings.push('No music files found under data/sound/music — the soundtrack ships loose on disc, not inside the .dat files.');
  }
  return { warnings };
}

async function detectGameType(files: SourceFile[]): Promise<GameType> {
  const master = files.find((f) => /^master\.dat$/i.test(f.path));
  if (!master) return 'fo2';
  const blob = await master.open();
  return (await sniffDat1(blob)) ? 'fo1' : 'fo2';
}

/** Batch writes: one IndexedDB transaction per ~48MB keeps the UI responsive. */
const BATCH_BYTES = 48 * 1024 * 1024;

export async function ingest(
  rawFiles: SourceFile[],
  source: InstallMeta['source'],
  onProgress: (p: ImportProgress) => void
): Promise<ImportResult> {
  const rooted = stripRoot(rawFiles);
  const gameType = await detectGameType(rooted);
  useVault(vaultDbName(gameType));
  const modsLayer = gameType === 'fo2' && hasModsLayer(rooted.map((f) => f.path));
  const kept = (p: string) => (isWanted(p) && !isQuarantined(p)) || keptDespiteQuarantine(p, modsLayer);
  const quarantined = rooted.filter((f) => isQuarantined(f.path) && !kept(f.path)).map((f) => f.path);
  const files = rooted.filter((f) => kept(f.path));
  const label = gameType === 'fo1' ? 'Fallout 1' : 'Fallout 2';
  if (!files.length) throw new ImportError(`No ${label} game files found in that selection.`);
  const { warnings } = validate(files, gameType);
  // Keep the old installation usable until the replacement has been fully
  // read and written. Only then remove paths the new selection does not carry.
  // Browser saves live in a separate IDBFS database and are never part of this
  // cleanup.
  const previousPaths = (await listFiles()).map((f) => f.path);

  const bytesTotal = files.reduce((n, f) => n + f.size, 0);
  let bytesDone = 0;
  let filesDone = 0;
  let batch: VaultEntry[] = [];
  let batchBytes = 0;

  const flush = async () => {
    if (!batch.length) return;
    await putFiles(batch);
    batch = [];
    batchBytes = 0;
  };

  for (const f of files) {
    onProgress({ stage: 'Copying', file: f.path, bytesDone, bytesTotal, filesDone, filesTotal: files.length });
    const blob = await f.open();
    batch.push({ path: f.path, size: blob.size, blob });
    batchBytes += blob.size;
    bytesDone += f.size;
    filesDone++;
    if (batchBytes >= BATCH_BYTES) {
      onProgress({ stage: 'Saving', file: f.path, bytesDone, bytesTotal, filesDone, filesTotal: files.length });
      await flush();
    }
    onProgress({ stage: 'Copying', file: f.path, bytesDone, bytesTotal, filesDone, filesTotal: files.length });
  }
  onProgress({ stage: 'Saving', bytesDone, bytesTotal, filesDone, filesTotal: files.length });
  await flush();

  const importedPaths = new Set(files.map((f) => f.path));
  const obsoletePaths = previousPaths.filter((path) => !importedPaths.has(path));
  if (obsoletePaths.length) {
    onProgress({
      stage: 'Removing old files',
      bytesDone,
      bytesTotal,
      filesDone,
      filesTotal: files.length,
    });
    await deleteFiles(obsoletePaths);
  }

  const names = new Set(files.map((f) => f.path.toLowerCase()));
  const meta: InstallMeta = {
    installedAt: Date.now(),
    source,
    fileCount: files.length,
    totalBytes: bytesTotal,
    hasPatch: names.has('patch000.dat'),
    dats: files.filter((f) => /^[^/]+\.dat$/i.test(f.path)).map((f) => f.path),
    gameType,
  };
  await setMeta('install', meta);
  // A fresh import earns a fresh patch prompt: "continue unpatched" was a
  // decision about the *previous* install, not a standing preference.
  await setMeta('unpatchedAcknowledged', false);
  // RPU is FO2-only. The RPU record follows the files. A selection carrying
  // mods/rpu.dat is a Restoration Project install (a desktop install with the
  // mod applied); one without has just replaced whatever was here, record
  // included.
  if (gameType === 'fo2') {
    const prevRpu = await getMeta<RpuMeta>(RPU_META_KEY);
    if (names.has('mods/rpu.dat')) {
      await setMeta(
        RPU_META_KEY,
        prevRpu ?? {
          version: 'unknown',
          installedAt: Date.now(),
          fileCount: files.filter((f) => /^mods\//i.test(f.path)).length,
          totalBytes: files.filter((f) => /^mods\//i.test(f.path)).reduce((n, f) => n + f.size, 0),
          mods: files.filter((f) => /^mods\/.+\.dat$/i.test(f.path)).map((f) => f.path.slice('mods/'.length)),
        } satisfies RpuMeta
      );
    } else if (prevRpu) {
      await setMeta(RPU_META_KEY, undefined);
    }
  } else {
    await setMeta(RPU_META_KEY, undefined);
  }
  registerGame({
    gameType,
    dbName: vaultDbName(gameType),
    installedAt: meta.installedAt,
    totalBytes: meta.totalBytes,
    fileCount: meta.fileCount,
  });

  onProgress({ stage: 'Done', bytesDone: bytesTotal, bytesTotal, filesDone: files.length, filesTotal: files.length });
  return { meta, warnings, quarantined };
}

/**
 * Boot-time repair for vaults populated before the quarantine existed.
 *
 * Returns the paths it evicted. Cheap: listFiles() reads keys and sizes, not
 * blobs, and the common case deletes nothing.
 */
export async function purgeContamination(): Promise<string[]> {
  const paths = (await listFiles()).map((f) => f.path);
  const modsLayer = hasModsLayer(paths);
  const bad = paths.filter((p) => isContamination(p) && !keptDespiteQuarantine(p, modsLayer));
  await deleteFiles(bad);
  return bad;
}

// --- source: <input webkitdirectory> ----------------------------------------

export function filesFromInput(list: FileList | File[]): SourceFile[] {
  return Array.from(list).map((file) => {
    const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath || file.name;
    return { path: rel.replace(/\\/g, '/'), size: file.size, open: async () => file };
  });
}

// --- source: File System Access API -----------------------------------------

export function directoryPickerAvailable(): boolean {
  return typeof (window as unknown as { showDirectoryPicker?: unknown }).showDirectoryPicker === 'function';
}

interface FsDirHandle {
  kind: 'directory';
  name: string;
  entries(): AsyncIterableIterator<[string, FsDirHandle | FsFileHandle]>;
}
interface FsFileHandle {
  kind: 'file';
  name: string;
  getFile(): Promise<File>;
}

export async function pickDirectory(): Promise<SourceFile[]> {
  const picker = (window as unknown as { showDirectoryPicker: (o?: object) => Promise<FsDirHandle> })
    .showDirectoryPicker;
  const root = await picker({ id: 'f2ce-gamedir', mode: 'read' });
  const out: SourceFile[] = [];
  const walk = async (dir: FsDirHandle, base: string) => {
    for await (const [name, handle] of dir.entries()) {
      if (name.startsWith('.')) continue;
      const path = base ? `${base}/${name}` : name;
      if (handle.kind === 'directory') {
        if (/^savegame/i.test(name)) continue;
        await walk(handle, path);
      } else {
        // Quarantined paths are collected too, so ingest() can report how many
        // mod-loader files it left behind — and so ddraw.ini is available when
        // a Restoration Project install earns its keep-despite-quarantine.
        if (!isWanted(path) && !isQuarantined(path)) continue;
        const file = await handle.getFile();
        out.push({ path, size: file.size, open: async () => file });
      }
    }
  };
  await walk(root, '');
  return out;
}

// --- source: dropped .zip ----------------------------------------------------

export async function filesFromZip(file: File): Promise<SourceFile[]> {
  let entries;
  try {
    entries = await readCentralDirectory(file);
  } catch (e) {
    if (e instanceof ZipReadError) throw new ImportError(`${e.message} Drop the folder instead, or unzip it first.`);
    throw e;
  }
  return entries
    .filter((e) => !e.isDirectory)
    .map((e) => ({
      path: e.name,
      size: e.uncompressedSize,
      open: () => entryBlob(file, e),
    }));
}

// --- source: .cue / .bin / .iso CD image -------------------------------------

export { filesFromCd, looksLikeCd, isCueFile, isImageFile, CdImportError } from './cd';
export type { CdLoadResult, DiscAssessment } from './cd';

// --- the 1.02 patch ----------------------------------------------------------

/**
 * Adding patch000.dat after the fact.
 *
 * A CD install is Fallout 2 v1.0, and v1.0 has bugs bad enough to strand a
 * playthrough. The official 1.02d patch is a single `patch000.dat` that the
 * engine stacks over master.dat, so applying it is exactly "put one more file in
 * the vault" — no merging, no version bookkeeping inside the archive.
 *
 * We never fetch it. The patch is Interplay's, and this app links to the
 * long-standing mirrors rather than redistributing or proxying a copy; the user
 * downloads it themselves and drops the file in. That keeps the shell's promise
 * that nothing here ships game content, and it means the bytes the engine loads
 * are the bytes the user chose to give it.
 */

export interface PatchResult {
  info: Dat2Info;
  /** Where inside a dropped archive we found it, when it was not the file itself. */
  extractedFrom?: string;
}

export class PatchError extends Error {}

const PATCH_NAME = 'patch000.dat';

/** Pulls patch000.dat out of a dropped .zip without inflating anything else. */
async function patchBlobFromZip(file: File): Promise<{ blob: Blob; from: string }> {
  let entries;
  try {
    entries = await readCentralDirectory(file);
  } catch (e) {
    throw new PatchError(e instanceof ZipReadError ? e.message : String(e));
  }
  const candidates = entries.filter((e) => !e.isDirectory && /(^|\/)patch000\.dat$/i.test(e.name));
  if (!candidates.length) {
    const names = entries
      .filter((e) => !e.isDirectory)
      .slice(0, 8)
      .map((e) => e.name);
    throw new PatchError(
      `${file.name} does not contain a patch000.dat. It holds: ${names.join(', ')}${entries.length > 8 ? ', …' : ''}. ` +
        'The official 1.02d download unpacks to patch000.dat plus a fallout2.exe and some readmes; only the .dat is any use here.'
    );
  }
  // Deepest-shallowest tie-break: a top-level copy beats one nested in a
  // "backup/" folder, which some repacks include.
  candidates.sort((a, b) => a.name.split('/').length - b.name.split('/').length);
  const chosen = candidates[0];
  return { blob: await entryBlob(file, chosen), from: `${file.name} → ${chosen.name}` };
}

/**
 * Validates and installs a dropped patch file. Accepts either a raw
 * patch000.dat or a .zip containing one.
 */
export async function installPatch(file: File): Promise<PatchResult> {
  let blob: Blob = file;
  let extractedFrom: string | undefined;

  if (/\.zip$/i.test(file.name)) {
    const found = await patchBlobFromZip(file);
    blob = found.blob;
    extractedFrom = found.from;
  }

  let info: Dat2Info;
  try {
    // A blob lifted out of a zip has already proven it is not another archive,
    // so it goes straight to the structural check; a dropped file has not.
    info = extractedFrom ? await readDat2Info(blob) : await acceptDat(blob, file.name);
  } catch (e) {
    throw e instanceof DatError ? new PatchError(e.message) : e;
  }

  await putFiles([{ path: PATCH_NAME, size: blob.size, blob }]);
  const meta = await getMeta<InstallMeta>('install');
  if (meta) {
    const dats = meta.dats.includes(PATCH_NAME) ? meta.dats : [...meta.dats, PATCH_NAME];
    await setMeta('install', {
      ...meta,
      hasPatch: true,
      dats,
      fileCount: meta.dats.includes(PATCH_NAME) ? meta.fileCount : meta.fileCount + 1,
      totalBytes: meta.totalBytes + blob.size,
    } satisfies InstallMeta);
  }
  return { info, extractedFrom };
}

/** Records that the player chose to play v1.0 anyway, so we stop nagging modally. */
export async function acknowledgeUnpatched(): Promise<void> {
  await setMeta('unpatchedAcknowledged', true);
}

export async function isUnpatchedAcknowledged(): Promise<boolean> {
  return (await getMeta<boolean>('unpatchedAcknowledged')) === true;
}

// --- source: dev endpoint (vite dev server only) -----------------------------

export async function filesFromDevEndpoint(): Promise<SourceFile[]> {
  const res = await fetch('/gamedata/__manifest.json');
  if (!res.ok) throw new ImportError('Dev game-data endpoint is not available (start `npm run dev`).');
  const manifest = (await res.json()) as { files: { path: string; size: number }[] };
  return manifest.files.map((f) => ({
    path: f.path,
    size: f.size,
    open: async () => {
      const r = await fetch('/gamedata/' + f.path.split('/').map(encodeURIComponent).join('/'));
      if (!r.ok) throw new ImportError(`dev endpoint: ${f.path} -> HTTP ${r.status}`);
      // `Response.blob()` rejects with a bare "Failed to fetch" on the
      // 333MB master.dat when the browser is driven through an automation
      // proxy, which reads as a server fault and is not one. Streaming the
      // same body works, so assemble the Blob ourselves.
      if (!r.body) return r.blob();
      const parts: BlobPart[] = [];
      const reader = r.body.getReader();
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value) parts.push(value);
      }
      return new Blob(parts);
    },
  }));
}
